/**
 * MenuManagement - Manage menu items and categories
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  UtensilsCrossed, Plus, Edit2, Trash2, Search, Filter,
  Leaf, Wheat, DollarSign, Clock, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['dining-categories'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/categories`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch menu items
  const { data: menuItems = [], isLoading } = useQuery({
    queryKey: ['dining-menu', filterCategory, showAvailableOnly, searchTerm],
    queryFn: async () => {
      let url = `${API_URL}/api/admin/dining/menu`;
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.append('category_id', filterCategory);
      if (showAvailableOnly) params.append('available_only', 'true');
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create menu item
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create item');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-menu']);
      queryClient.invalidateQueries(['dining-categories']);
      queryClient.invalidateQueries(['dining-stats']);
      setShowAddDialog(false);
      toast.success('Menu item created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update menu item
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update item');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-menu']);
      setEditingItem(null);
      toast.success('Menu item updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Toggle availability
  const toggleAvailabilityMutation = useMutation({
    mutationFn: async ({ id, isAvailable }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu/${id}/availability`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ is_available: isAvailable })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to toggle');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-menu']);
      queryClient.invalidateQueries(['dining-stats']);
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete menu item
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-menu']);
      queryClient.invalidateQueries(['dining-categories']);
      queryClient.invalidateQueries(['dining-stats']);
      toast.success('Menu item deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const MenuItemForm = ({ item, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: item?.name || '',
      description: item?.description || '',
      category_id: item?.category_id || '',
      price: item?.price || 0,
      cost_price: item?.cost_price || 0,
      is_available: item?.is_available ?? true,
      is_vegetarian: item?.is_vegetarian || false,
      is_vegan: item?.is_vegan || false,
      is_gluten_free: item?.is_gluten_free || false,
      allergens: item?.allergens || [],
      preparation_time: item?.preparation_time || 10,
      calories: item?.calories || null,
      tags: item?.tags || []
    });

    const allergenOptions = ['Nuts', 'Dairy', 'Eggs', 'Soy', 'Wheat', 'Fish', 'Shellfish'];
    const tagOptions = ['Popular', 'Spicy', 'New', 'Chef Special', 'Healthy', 'Kids'];

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
      >
        <div>
          <label className="text-sm font-medium">Item Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Grilled Chicken Sandwich"
            data-testid="input-menu-item-name"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of the dish"
            rows={2}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Category *</label>
          <Select
            value={formData.category_id}
            onValueChange={(value) => setFormData({ ...formData, category_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Price ($) *</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Cost Price ($)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.cost_price}
              onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
              placeholder="For profit tracking"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Prep Time (mins)</label>
            <Input
              type="number"
              min="1"
              value={formData.preparation_time}
              onChange={(e) => setFormData({ ...formData, preparation_time: parseInt(e.target.value) || 10 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Calories</label>
            <Input
              type="number"
              min="0"
              value={formData.calories || ''}
              onChange={(e) => setFormData({ ...formData, calories: parseInt(e.target.value) || null })}
              placeholder="Optional"
            />
          </div>
        </div>

        {/* Dietary Options */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Dietary Info</label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={formData.is_vegetarian}
                onCheckedChange={(checked) => setFormData({ ...formData, is_vegetarian: checked })}
              />
              <span className="text-sm">🥬 Vegetarian</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={formData.is_vegan}
                onCheckedChange={(checked) => setFormData({ ...formData, is_vegan: checked })}
              />
              <span className="text-sm">🌱 Vegan</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={formData.is_gluten_free}
                onCheckedChange={(checked) => setFormData({ ...formData, is_gluten_free: checked })}
              />
              <span className="text-sm">🌾 Gluten-Free</span>
            </label>
          </div>
        </div>

        {/* Allergens */}
        <div>
          <label className="text-sm font-medium mb-2 block">Allergens</label>
          <div className="flex flex-wrap gap-2">
            {allergenOptions.map((allergen) => (
              <Badge
                key={allergen}
                variant={formData.allergens.includes(allergen.toLowerCase()) ? 'destructive' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  const lower = allergen.toLowerCase();
                  setFormData({
                    ...formData,
                    allergens: formData.allergens.includes(lower)
                      ? formData.allergens.filter(a => a !== lower)
                      : [...formData.allergens, lower]
                  });
                }}
              >
                {allergen}
              </Badge>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="text-sm font-medium mb-2 block">Tags</label>
          <div className="flex flex-wrap gap-2">
            {tagOptions.map((tag) => (
              <Badge
                key={tag}
                variant={formData.tags.includes(tag.toLowerCase()) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  const lower = tag.toLowerCase();
                  setFormData({
                    ...formData,
                    tags: formData.tags.includes(lower)
                      ? formData.tags.filter(t => t !== lower)
                      : [...formData.tags, lower]
                  });
                }}
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-sm">Available for Order</p>
            <p className="text-xs text-gray-500">Toggle off to temporarily hide from menu</p>
          </div>
          <Switch
            checked={formData.is_available}
            onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading || !formData.category_id} data-testid="submit-menu-item-btn">
            {isLoading ? 'Saving...' : item ? 'Update Item' : 'Create Item'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="menu-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Menu Items</h2>
          <p className="text-sm text-gray-500">Manage your food and beverage menu</p>
        </div>
        <Button 
          onClick={() => setShowAddDialog(true)} 
          disabled={categories.length === 0}
          data-testid="add-menu-item-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search menu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch
            checked={showAvailableOnly}
            onCheckedChange={setShowAvailableOnly}
          />
          <span className="text-sm">Available only</span>
        </label>
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No categories configured</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add categories first before creating menu items
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading menu...</div>
      ) : menuItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No menu items</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add your first menu item to get started
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {menuItems.map((item) => (
            <Card 
              key={item.id} 
              className={`${!item.is_available ? 'opacity-60' : ''}`}
              data-testid={`menu-item-card-${item.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{item.name}</h3>
                      {item.is_special && (
                        <Badge variant="warning" className="text-xs">⭐ Special</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{item.category_name}</p>
                  </div>
                  <div className="text-right">
                    {item.special_price ? (
                      <>
                        <p className="text-sm line-through text-gray-400">${item.price?.toFixed(2)}</p>
                        <p className="text-lg font-bold text-green-600">${item.special_price?.toFixed(2)}</p>
                      </>
                    ) : (
                      <p className="text-lg font-bold">${item.price?.toFixed(2)}</p>
                    )}
                  </div>
                </div>

                {item.description && (
                  <p className="text-sm text-gray-500 mb-2 line-clamp-2">{item.description}</p>
                )}

                {/* Dietary badges */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {item.is_vegetarian && <Badge variant="outline" className="text-xs">🥬 Veg</Badge>}
                  {item.is_vegan && <Badge variant="outline" className="text-xs">🌱 Vegan</Badge>}
                  {item.is_gluten_free && <Badge variant="outline" className="text-xs">GF</Badge>}
                  {item.tags?.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs capitalize">{tag}</Badge>
                  ))}
                </div>

                {/* Info row */}
                <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {item.preparation_time}m
                  </span>
                  {item.calories && (
                    <span>{item.calories} cal</span>
                  )}
                  {item.total_orders > 0 && (
                    <span>{item.total_orders} orders</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleAvailabilityMutation.mutate({
                      id: item.id,
                      isAvailable: !item.is_available
                    })}
                  >
                    {item.is_available ? (
                      <><Eye className="h-4 w-4 mr-1" /> Available</>
                    ) : (
                      <><EyeOff className="h-4 w-4 mr-1" /> Hidden</>
                    )}
                  </Button>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditingItem(item)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => {
                        if (confirm('Delete this menu item?')) {
                          deleteMutation.mutate(item.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Menu Item</DialogTitle>
          </DialogHeader>
          <MenuItemForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Menu Item</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <MenuItemForm
              item={editingItem}
              onSubmit={(data) => updateMutation.mutate({ id: editingItem.id, data })}
              onCancel={() => setEditingItem(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuManagement;
